import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { FormContainer, TextInput, SelectInput, ToggleSwitchBtn } from "../../shared";
import useGlobalMutation from "../../../hooks/useGlobalMutation";
import { CREATE_BASIC_LOGIN_USER, GET_BASIC_LOGIN_APPS } from "./query";
import { Helper, RegexPattern, clearCache } from "../../../utils";
import { useQuery, gql } from "@apollo/client";
import { toast } from "react-toastify";

const CreateBasicLoginUser = () => {
    const currentUserType = Number(useSelector((state) => state.users.currentUser.userType));
    const getUrl = currentUserType === 99 ? "/user-list" : "";
    const [applications, setApplications] = useState([]);
    const [domains, setDomains] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        watch,
        setValue,
        setError,
        clearErrors,
    } = useForm({
        mode: "onChange",
    });

    const applicationName = watch("applicationName", "");
    const sendToEmail = watch("sendToEmail", false);
    const password = watch("password", "");
    const confirmPassword = watch("confirmPassword", "");

    const { data, loading: basicLoginAppLoading } = useQuery(gql(GET_BASIC_LOGIN_APPS));

    useEffect(() => {
        if (data?.allBasicAuths?.nodes.length > 0) {
            const basicLoginApps = data.allBasicAuths.nodes;
            const appNameList = [];

            if (basicLoginApps.length > 1) {
                basicLoginApps.map((item) => {
                    if (!appNameList.includes(item.appName)) {
                        appNameList.push(item.appName);
                    }
                    return null;
                });
            }

            appNameList.forEach((item) => {
                setApplications((prevState) => [
                    ...prevState,
                    {
                        id: item,
                        name: Helper.getAppNameById(item),
                    },
                ]);
            });
        }
    }, [data]);

    useEffect(() => {
        setDomains([]);
        setValue("domainName", "");

        if (data?.allBasicAuths?.nodes.length > 0) {
            const basicLoginApps = data.allBasicAuths.nodes;

            basicLoginApps.map((item) => {
                if (item.appName === applicationName && !domains.includes(item.domainName)) {
                    setDomains((prevState) => [
                        ...prevState,
                        {
                            id: item.domainName,
                            name: item.domainName,
                        },
                    ]);
                }
                return null;
            });
        }
    }, [applicationName]);

    useEffect(() => {
        if (confirmPassword.length > 0 && password !== confirmPassword && !errors.password) {
            setError("confirmPassword", { message: "Passwords do not match" });
        } else {
            clearErrors("confirmPassword");
        }
    }, [password]);

    const { submitMutation, loading } = useGlobalMutation(CREATE_BASIC_LOGIN_USER, (cache) => {
        clearCache(cache, "getBasicLoginUser");
    });

    const submitAction = handleSubmit(async (data) => {
        const {
            firstName,
            lastName,
            email,
            username,
            phone,
            gender,
            applicationName,
            domainName,
            sendToEmail,
            password,
            confirmPassword,
        } = data;

        if (sendToEmail || password === confirmPassword) {
            try {
                const variables = {
                    isEamil: sendToEmail ? 1 : 0,
                    password,
                    basicLoginUser: {
                        firstName,
                        lastName,
                        email,
                        username: username.trim(),
                        phone,
                        gender: gender.toUpperCase() || "",
                        userType: 1,
                        status: 1,
                        basicLoginUserPermissions: applicationName ? [applicationName] : [],
                        domainName: domainName ? [domainName] : [],
                    },
                };
                const isResultNull = await submitMutation(
                    variables,
                    "insertAdminBasicLoginUser",
                    "User created successfully"
                );

                isResultNull === null && reset();
            } catch (error) {
                toast.error(error.message);
            }
        }
    });

    return (
        <FormContainer
            customClass="custom-shadow p-5"
            submitAction={submitAction}
            loading={loading || basicLoginAppLoading}
            backUrl={`${getUrl}/basic-login`}
            submitButtonText="Create User"
        >
            <h3 className="mb-5">Create Basic Login User</h3>

            <div className="row">
                <div className="col-md-6">
                    <TextInput
                        id="firstName"
                        labelText="First Name"
                        {...register("firstName", {
                            required: true,
                            pattern: RegexPattern.name,
                        })}
                        isInvalid={!!errors.firstName}
                        required
                    />
                </div>

                <div className="col-md-6">
                    <TextInput
                        id="lastName"
                        labelText="Last Name"
                        {...register("lastName", {
                            required: true,
                            pattern: RegexPattern.name,
                        })}
                        isInvalid={!!errors.lastName}
                        required
                    />
                </div>

                <div className="col-md-6">
                    <TextInput
                        id="email"
                        type="email"
                        labelText="Email"
                        {...register("email", {
                            required: true,
                            pattern: RegexPattern.email,
                        })}
                        isInvalid={!!errors.email}
                        required
                    />
                </div>

                <div className="col-md-6">
                    <TextInput
                        id="username"
                        labelText="User Name"
                        {...register("username", {
                            required: true,
                        })}
                        isInvalid={!!errors.username}
                        required
                    />
                </div>

                <div className="col-md-6">
                    <TextInput
                        id="phone"
                        type="number"
                        labelText="Phone"
                        {...register("phone", {
                            pattern: RegexPattern.number,
                        })}
                        isInvalid={!!errors.phone}
                    />
                </div>

                <div className="col-md-6">
                    <SelectInput
                        id="gender"
                        labelText="Gender"
                        {...register("gender", {})}
                        options={[
                            { id: "M", name: "Male" },
                            { id: "F", name: "Female" },
                        ]}
                        isInvalid={!!errors.gender}
                    />
                </div>

                <div className="col-md-6">
                    <SelectInput
                        id="application"
                        labelText="Application"
                        {...register("applicationName", {
                            required: true,
                        })}
                        options={applications}
                        isInvalid={!!errors.applicationName}
                        required
                    />
                </div>

                <div className="col-md-6">
                    <SelectInput
                        id="domainName"
                        labelText="Domain Name"
                        {...register("domainName", {
                            required: true,
                        })}
                        options={domains}
                        isInvalid={!!errors.domainName}
                        required
                    />
                </div>

                <div className="col-12">
                    <div className="d-flex justify-content-between">
                        <label className="mb-2">
                            Send Set Password Link To this User Email Address
                        </label>

                        <Controller
                            name="sendToEmail"
                            control={control}
                            render={({ field, ref }) => (
                                <ToggleSwitchBtn
                                    id="sendToEmail"
                                    name="sendToEmail"
                                    {...field}
                                    ref={ref}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="col-md-6">
                    <Controller
                        name="password"
                        control={control}
                        render={({ field: { ref } }) => (
                            <TextInput
                                id="password"
                                labelText="Password"
                                type="password"
                                isInvalid={!!errors.password && !sendToEmail}
                                required={!sendToEmail}
                                errorMessage={
                                    (!sendToEmail && errors.password && errors.password.message) ||
                                    "Invalid Password"
                                }
                                disabled={sendToEmail}
                                ref={ref}
                                {...register("password", {
                                    required: !sendToEmail,
                                    pattern: RegexPattern.password,
                                })}
                            />
                        )}
                    />
                </div>
                <div className="col-md-6">
                    <Controller
                        name="confirmPassword"
                        control={control}
                        render={({ field: { ref } }) => (
                            <TextInput
                                id="confirmPassword"
                                labelText="Confirm Password"
                                type="password"
                                isInvalid={!!errors.confirmPassword && !sendToEmail}
                                required={!sendToEmail}
                                errorMessage={
                                    (!sendToEmail &&
                                        errors.confirmPassword &&
                                        errors.confirmPassword.message) ||
                                    "Invalid Password"
                                }
                                ref={ref}
                                disabled={sendToEmail}
                                {...register("confirmPassword", {
                                    required: !sendToEmail,
                                    validate: (value) =>
                                        value === password ||
                                        sendToEmail ||
                                        "Passwords do not match",
                                })}
                            />
                        )}
                    />
                </div>
            </div>
        </FormContainer>
    );
};

export default CreateBasicLoginUser;
